import {
  EnvironmentProviders,
  InjectionToken,
  Optional,
  makeEnvironmentProviders,
} from '@angular/core';
import type { Functions as FbFunctions } from 'firebase/functions';
import {
  connectFunctionsEmulator as rawConnectFunctionsEmulator,
  getFunctions as rawGetFunctions,
  httpsCallable as rawHttpsCallable,
  httpsCallableFromURL as rawHttpsCallableFromURL,
} from 'firebase/functions';
import { kdvWrap } from '@fido/firebase';
import { FirebaseApp } from '@fido/firebase/app';
import { AppCheck } from '@fido/firebase/app-check';
import { Auth } from '@fido/firebase/auth';

// Wrapped firebase/functions functions. Mirrors @angular/fire's
// _zoneWrap(fn, true) declarations from angular-fire-functions.mjs.
export const connectFunctionsEmulator = kdvWrap(rawConnectFunctionsEmulator, true);
export const getFunctions = kdvWrap(rawGetFunctions, true);
export const httpsCallable = kdvWrap(rawHttpsCallable, true);
export const httpsCallableFromURL = kdvWrap(rawHttpsCallableFromURL, true);

export type { HttpsCallable, HttpsCallableResult, HttpsCallableOptions } from 'firebase/functions';

export type Functions = FbFunctions;
export const Functions = new InjectionToken<FbFunctions>('@fido/firebase.functions');

export function provideFunctions(factory: () => FbFunctions): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: Functions,
      // FirebaseApp + (Optional) Auth + (Optional) AppCheck — mirrors
      // @angular/fire's provideFunctions multi-instance deps. Upstream
      // comment: "Defensively load Auth first, if provided" — ensures
      // auth.currentUser is restored from persistence before callables
      // fire (avoids 401 Unauthenticated on cold-load callable invocations).
      // AppCheck dep similarly gives the token mint a head start.
      useFactory: (_app: unknown, _auth: unknown, _appCheck: unknown) => factory(),
      deps: [FirebaseApp, [new Optional(), Auth], [new Optional(), AppCheck]],
    },
  ]);
}
